import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Event from '@fuww/library/src/Layouts/EventPage';
import { Breadcrumbs } from '@fuww/library/src/Breadcrumb';
import Button from '@fuww/library/src/Button';
import { FormattedDate, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import Head from '../Head';
import withQuery from '../../lib/withQuery';
import throw404 from '../../lib/throw404';
import getEventJsonLdData from '../../lib/getEventJsonLdData';
import messages from '../../lib/messages.json';
import HomeLink from '../HomeLink';
import LinkAndAnchor from '../LinkAndAnchor';
import { useSiteContext } from '../SiteContext';
import Image from '../Image';
import { DEFAULT_EVENT_IMAGES } from '../../lib/constants';
import { getRouterState } from '../../lib/savedRouterState';
import getProxiedImages from '../../lib/getProxiedImages';

export const imageConfigurations = [
  {
    width: 42,
    height: 28,
    quality: 10,
    resize: 'FILL',
  },
  {
    width: 375, height: 250, quality: 70, resize: 'FILL',
  },
  {
    width: 414, height: 276, quality: 70, resize: 'FILL',
  },
  {
    width: 720, height: 480, quality: 70, resize: 'FILL',
  },
];

const variables = {
  imageConfigurations,
};

const {
  imageUrls: fallbackImageUrls,
  srcSet: fallbackSourceSet,
} = getProxiedImages(variables, DEFAULT_EVENT_IMAGES);

const EventDetails = ({
  data: { eventBySlug },
}) => {
  const intl = useIntl();
  const { url } = useSiteContext();
  const router = useRouter();
  const [lastEventsQuery, setLastEventsQuery] = useState(null);

  useEffect(() => {
    setLastEventsQuery({ ...getRouterState('events')?.query });
  }, []);

  if (!eventBySlug) return throw404();

  const {
    id,
    title,
    body,
    description,
    editions,
    imageUrl,
    imageUrls,
    websiteUrl,
    slug,
  } = eventBySlug;

  const firstEdition = editions?.[0];
  const startDate = firstEdition?.startsAt;
  const endDate = firstEdition?.endsAt;
  const location = [
    firstEdition?.buildingName,
    firstEdition?.address,
    firstEdition?.city,
    firstEdition?.country,
  ].filter(Boolean).join(', ');

  const formattedEditions = editions?.map((edition) => {
    const editionStartDate = new Date(edition.startsAt);
    const editionEndDate = new Date(edition.endsAt);
    const year = editionStartDate.getFullYear();

    const startMonth = editionStartDate.toLocaleString('en-US', {
      month: 'short',
    });
    const startDay = editionStartDate.getDate();
    const endMonth = editionEndDate.toLocaleString('en-US', {
      month: 'short',
    });
    const endDay = editionEndDate.getDate();

    const dateRange = `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${
      year
    }`;

    const editionLabel = `${edition.title} (${dateRange})`;

    return {
      id: edition.id,
      value: String(edition.id),
      label: editionLabel,
      title: edition.title,
      startsAt: edition.startsAt,
      endsAt: edition.endsAt,
      formattedStartDate: (
        <FormattedDate
          value={edition.startsAt}
          year="numeric"
          month="long"
          day="numeric"
        />
      ),
      formattedEndDate: (
        <FormattedDate
          value={edition.endsAt}
          year="numeric"
          month="long"
          day="numeric"
        />
      ),
      description: edition.description,
      markdown: edition.markdown ? (
        // eslint-disable-next-line react/no-danger
        <div dangerouslySetInnerHTML={{ __html: edition.markdown }} />
      ) : undefined,
      buildingName: edition.buildingName,
      address: edition.address,
      city: edition.city,
      postalCode: edition.postalCode,
      addressRegion: edition.addressRegion,
      country: edition.country,
    };
  });

  const hasCustomImage = imageUrl && imageUrls && imageUrls.length > 0;
  const eventImageUrls = hasCustomImage ? imageUrls : fallbackImageUrls;
  const eventImage = hasCustomImage
    ? eventImageUrls[3]
    : DEFAULT_EVENT_IMAGES[3];

  const placeholder = eventImageUrls[0];
  const sourceSet = hasCustomImage
    ? `${eventImageUrls[1]} 375w, ${eventImageUrls[2]} 414w, ${
      eventImageUrls[3]
    } 720w`
    : fallbackSourceSet;

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor
      route="events"
      passHref
    >
      {intl.formatMessage(messages.events)}
    </LinkAndAnchor>,
    <LinkAndAnchor
      route="event"
      passHref
      params={{ slug }}
    >
      {title}
    </LinkAndAnchor>,
  ];

  return (
    <>
      <Head
        title={title}
        description={description
          || intl.formatMessage(messages.eventsMetaDescription)}
        jsonLdData={getEventJsonLdData(intl, {
          ...eventBySlug,
          startDate,
          endDate,
        }, url, eventImage)}
        subjectType="Event"
        subjectId={id}
      />
      <Event
        breadcrumbs={(
          <Breadcrumbs
            crumbs={breadcrumbs}
          />
        )}
        title={title}
        content={(
          // eslint-disable-next-line react/no-danger
          <div dangerouslySetInnerHTML={{ __html: body }} />
        )}
        website={websiteUrl}
        location={location}
        editions={formattedEditions}
        image={(
          <Image
            src={eventImage}
            srcSet={sourceSet}
            placeholder={placeholder}
            alt={title}
            title={title}
            width="100%"
            height="240px"
            sizes="(min-width: 768px) 720px, 100vw"
            finalFetchpriority="high"
            placeholderFetchpriority="high"
            loadStrategy="instant"
            preload
          />
        )}
        formattedStartDate={(
          <FormattedDate
            value={startDate}
            year="numeric"
            month="long"
            day="numeric"
          />
        )}
        formattedEndDate={(
          <FormattedDate
            value={endDate}
            year="numeric"
            month="long"
            day="numeric"
          />
        )}
        backButton={(
          <LinkAndAnchor
            route="events"
            passHref
            params={lastEventsQuery}
            onClick={(event_) => {
              if (lastEventsQuery) {
                event_.preventDefault();

                router.back();
              }
            }}
            scroll={!lastEventsQuery}
            useRouter
          >
            <Button
              variant="text"
              className="event-detail-backbutton"
            >
              EVENT CALENDAR
            </Button>
          </LinkAndAnchor>
        )}
      />
    </>
  );
};

EventDetails.propTypes = {
  data: PropTypes.shape({
    eventBySlug: PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      body: PropTypes.string,
      description: PropTypes.string,
      imageUrl: PropTypes.string,
      imageUrls: PropTypes.arrayOf(PropTypes.string),
      websiteUrl: PropTypes.string,
      slug: PropTypes.string,
      editions: PropTypes.arrayOf(PropTypes.shape({
        startsAt: PropTypes.string,
        endsAt: PropTypes.string,
        buildingName: PropTypes.string,
        address: PropTypes.string,
        city: PropTypes.string,
        country: PropTypes.string,
      })),
    }),
  }).isRequired,
};

export default withQuery(EventDetails);
